import * as React from 'react';
import Stack from '@mui/material/Stack';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import moment from 'moment';

import { GetEventDetailedQuery } from '@graphql';
import IDCard from './IDCard';
import { EventPreviewManage } from './PreviewManage';
import { Tiles, Wrapper, WrapperContext } from '../../common';

interface EventPreviewProps {
  event: GetEventDetailedQuery['event'];
  scrollToTickets: () => void;
}

function EventPreview(props: EventPreviewProps) {
  const { event, scrollToTickets } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { t } = useTranslation('account');

  if (!event) {
    return null;
  }

  return (
    <Stack spacing={isMobile ? 2 : 5}>
      <IDCard event={event} scrollToTickets={scrollToTickets} />
      <Wrapper type="preview">
        <Wrapper.Header title={t('menu.Preview')} />
        <Wrapper.Body disablePadding={true} sx={{ p: 0, backgroundColor: 'transparent' }}>
          <EventPreviewInner {...props} />
        </Wrapper.Body>
      </Wrapper>
    </Stack>
  );
}

interface EventPreviewInnerProps {
  event: GetEventDetailedQuery['event'];
}

function EventPreviewInner(props: EventPreviewInnerProps) {
  const context = React.useContext(WrapperContext);

  if (!context) {
    throw new Error('EventPreviewInner must be used inside Wrapper');
  }

  const { isManaging } = context;
  const { event } = props;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const direction = isMobile ? 'column' : 'row';

  if (!event) {
    return null;
  }

  const { tickets, address, from, to, spot, status, updates } = event;

  const previousFrom = updates.find((u) => u.from && !moment(u.from).isSame(moment(from)))?.from;
  const previousTo = updates.find((u) => u.to && !moment(u.to).isSame(moment(to)))?.to;
  const previousStatus =
    updates.find((u, idx) => u.status && idx !== updates.length - 1)?.status || undefined;

  return (
    <Stack spacing={2}>
      {!isManaging ? (
        <Stack spacing={1}>
          <Stack direction={direction} spacing={1}>
            <Tiles.Date
              from={from}
              to={to}
              previousFrom={previousFrom}
              previousTo={previousTo}
              status={status}
              previousStatus={previousStatus}
            />
            <Tiles.Schedule
              from={from}
              to={to}
              previousFrom={previousFrom}
              previousTo={previousTo}
              status={status}
              previousStatus={previousStatus}
            />
          </Stack>
          <Stack direction={direction} spacing={1}>
            <Tiles.EventPrice tickets={tickets} />
            <Tiles.Address label={spot?.address?.label || address?.label} />
          </Stack>
          {spot && (
            <Stack direction={direction} spacing={1}>
              <Tiles.Spot avatar={spot.avatar?.url} name={spot.name} slug={spot.slug} />
            </Stack>
          )}
        </Stack>
      ) : (
        <EventPreviewManage event={event} />
      )}
    </Stack>
  );
}

export default React.memo(EventPreview);
